import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useInstallPrompt } from "@/hooks/useInstallPrompt";

const AUTO_INSTALL_PARAM = "install";

const closeTab = (fallback: () => void) => {
  window.close();
  // window.close() is silently ignored on tabs not opened via script —
  // fall back to revealing the app so the user isn't stuck on the gate.
  setTimeout(() => {
    if (!window.closed) fallback();
  }, 300);
};

/**
 * Cross-origin links (e.g. the marketing site) can't fire another origin's
 * install prompt directly, so they land here with ?install=1. This hook
 * shows a gate screen so the visitor sees an install ask instead of the
 * app UI — the Install button is user-gesture-triggered so Chrome actually
 * shows the native dialog (auto-calling prompt() from an effect is silently
 * dismissed by Chrome's user-activation requirement).
 */
export const useInstallGate = () => {
  const { canInstall, isStandalone, isIos, isMacSafari, promptInstall } =
    useInstallPrompt();
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsAutoInstall = searchParams.get(AUTO_INSTALL_PARAM) === "1";
  const [isGating, setIsGating] = useState(wantsAutoInstall);
  const resolvedRef = useRef(false);

  const stopGating = useCallback(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete(AUTO_INSTALL_PARAM);
    setSearchParams(next, { replace: true });
    setIsGating(false);
  }, [searchParams, setSearchParams]);

  // If already running as installed standalone app, no need to gate
  useEffect(() => {
    if (wantsAutoInstall && isStandalone) stopGating();
  }, [wantsAutoInstall, isStandalone, stopGating]);

  // "Cancel" button on the gate screen
  const handleCancel = useCallback(() => {
    closeTab(stopGating);
  }, [stopGating]);

  // "Install" button click — must be called from a user gesture so Chrome
  // shows the native dialog. After user responds (accept or cancel), close
  // the tab since it was opened solely for the install flow.
  const handleInstall = useCallback(async () => {
    await promptInstall().catch(() => {});
    closeTab(stopGating);
  }, [promptInstall, stopGating]);

  return {
    isGating,
    canInstall,
    isIos,
    isMacSafari,
    handleInstall,
    handleCancel,
  };
};
